/**
 * 变体 Metafields 处理器
 * 负责读取、更新和管理当前变体的 metafields 数据
 */
class VariantMetafieldsHandler {
  constructor() {
    this.currentVariant = null;
    this.currentMetafields = {};
    this.specialDeliveryData = {};
    this.normalDeliveryData = {}; // 添加普通配送数据
    
    // 等待 DOM 完全加载后再初始化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // DOM 已经加载完成
      this.init();
    }
  }

  init() {
    this.loadInitialData();
    this.setupEventListeners();
  }

  /**
   * 加载初始数据
   */
  loadInitialData() {
    // 从页面获取产品和变体信息
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const productId = productInfo.dataset.id;
      const sectionId = productInfo.dataset.section;
      
      // 获取当前选中的变体
      const variantIdInput = document.querySelector('.product-variant-id');
      if (variantIdInput) {
        const variantId = variantIdInput.value;
        this.loadVariantMetafields(variantId);
      }
    }

    // 加载特惠配送数据
    this.loadSpecialDeliveryData();
    
    // 加载普通配送数据
    this.loadNormalDeliveryData();
  }

  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    // 监听变体变化事件
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });

    // 监听变体更新事件（如果存在其他系统触发的变体更新）
    document.addEventListener('variant:updated', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });
    
    // 监听配送方式选择事件
    document.addEventListener('delivery-method:change', (event) => {
      if (event.detail && event.detail.method) {
        this.handleDeliveryMethodChange(event.detail.method);
      }
    });
  }

  /**
   * 处理变体变化
   * @param {Object} variant 新的变体对象
   */
  handleVariantChange(variant) {
    this.currentVariant = variant;
    
    if (variant && variant.id) {
      // 变体存在，加载其 metafields
      this.loadVariantMetafields(variant.id);
    } else {
      // 变体不存在，清理 metafields 数据
      this.currentMetafields = {};
      this.dispatchMetafieldsUpdated();
      this.updateSpecialDeliveryOptions();
      this.updateNormalDeliveryOptions();
    }
  }
  
  /**
   * 处理配送方式变化
   * @param {string} method 配送方式
   */
  handleDeliveryMethodChange(method) {
    // 根据选择的配送方式更新相应的配送选项
    if (method === 'special') {
      this.updateSpecialDeliveryOptions();
    } else if (method === 'time') {
      this.updateNormalDeliveryOptions();
    }
  }

  /**
   * 加载变体的 metafields
   * @param {string|number} variantId 变体ID
   */
  loadVariantMetafields(variantId) {
    // 直接使用 Storefront API 获取变体元字段
    this.loadVariantMetafieldsFromAPI(variantId);
  }

  /**
   * 使用 Storefront API 获取变体的元字段
   * @param {string|number} variantId 变体ID
   */
  async loadVariantMetafieldsFromAPI(variantId) {
    try {
      const shop = window.Shopify.shop;
      const storefrontApiUrl = `https://${shop}/api/2025-07/graphql.json`;
      const query = `
        query GetVariantMetafields($variantId: ID!) {
          node(id: $variantId) {
            ... on ProductVariant {
              id
              title
              metafields(identifiers: [
                {namespace: "powerness_sp", key: "type"},
                {namespace: "spname", key: "normal_de"}
              ]) {
                namespace
                key
                type
                value
              }
            }
          }
        }
      `;

      const variables = {
        variantId: `gid://shopify/ProductVariant/${variantId}`
      };

      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 如果有访问令牌，则添加到请求头
      if (window.Shopify.storefrontAccessToken) {
        headers['X-Shopify-Storefront-Access-Token'] = window.Shopify.storefrontAccessToken;
      }

      const response = await fetch(storefrontApiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        // 初始化空的 metafields 对象
        this.currentMetafields = {};
        
        // 触发 metafields 更新事件
        this.dispatchMetafieldsUpdated();
        
        // 更新配送选项
        this.updateSpecialDeliveryOptions();
        this.updateNormalDeliveryOptions();
        return;
      }

      const variant = data.data.node;
      if (variant && variant.id) {
        this.currentVariant = {
          id: variantId,
          title: variant.title
        };
        
        // 解析 API 返回的 metafields
        this.currentMetafields = {};
        if (variant.metafields) {
          variant.metafields.forEach(metafield => {
            if (metafield.namespace === 'powerness_sp' && metafield.key === 'type') {
              this.currentMetafields.special_delivery_key = metafield.value;
            }
            if (metafield.namespace === 'spname' && metafield.key === 'normal_de') {
              this.currentMetafields.normal_delivery_key = metafield.value;
            }
          });
        }
        
        // 触发 metafields 更新事件
        this.dispatchMetafieldsUpdated();
        
        // 更新配送选项
        this.updateSpecialDeliveryOptions();
        this.updateNormalDeliveryOptions();
      } else {
        console.warn(`Variant ${variantId} not found via API`);
        // 初始化空的 metafields 对象
        this.currentMetafields = {};
        
        // 触发 metafields 更新事件
        this.dispatchMetafieldsUpdated();
        
        // 更新配送选项
        this.updateSpecialDeliveryOptions();
        this.updateNormalDeliveryOptions();
      }
    } catch (error) {
      console.error('Error loading variant metafields from API:', error);
      // 初始化空的 metafields 对象
      this.currentMetafields = {};
      
      // 触发 metafields 更新事件
      this.dispatchMetafieldsUpdated();
      
      // 更新配送选项
      this.updateSpecialDeliveryOptions();
      this.updateNormalDeliveryOptions();
    }
  }

  /**
   * 加载特惠配送数据
   */
  loadSpecialDeliveryData() {
    // 从页面的 liquid 数据中获取特惠配送信息
    const specialDeliveryElement = document.querySelector('[data-special-delivery]');
    if (specialDeliveryElement) {
      try {
        this.specialDeliveryData = JSON.parse(specialDeliveryElement.textContent);
      } catch (error) {
        console.error('Error parsing special delivery data:', error);
        this.specialDeliveryData = {};
      }
    }
  }
  
  /**
   * 加载普通配送数据
   */
  loadNormalDeliveryData() {
    // 从页面的 liquid 数据中获取普通配送信息
    const normalDeliveryElement = document.querySelector('[data-normal-delivery]');
    if (normalDeliveryElement) {
      try {
        this.normalDeliveryData = JSON.parse(normalDeliveryElement.textContent);
      } catch (error) {
        console.error('Error parsing normal delivery data:', error);
        this.normalDeliveryData = {};
      }
    }
  }

  /**
   * 获取当前变体的特惠配送信息
   * @returns {Object|null} 特惠配送信息
   */
  getCurrentSpecialDelivery() {
    // 获取当前变体的 metafield 值作为 key
    const rawDeliveryKey = this.getVariantMetafieldValue('special_delivery_key');
    
    let deliveryKey = null;
    try {
      deliveryKey = JSON.parse(rawDeliveryKey)?.[0] || null;
    } catch (error) {
      deliveryKey = rawDeliveryKey; // 如果不是JSON，直接使用原始值
    }
    
    if (deliveryKey && this.specialDeliveryData[deliveryKey]) {
      const specialDelivery = this.specialDeliveryData[deliveryKey];
      return specialDelivery;
    }
    
    return null;
  }
  
  /**
   * 获取当前变体的普通配送信息
   * @returns {Object|null} 普通配送信息
   */
  getCurrentNormalDelivery() {
    // 获取当前变体的 metafield 值作为 key
    const rawDeliveryKey = this.getVariantMetafieldValue('normal_delivery_key');
    
    let deliveryKey = null;
    try {
      deliveryKey = JSON.parse(rawDeliveryKey)?.[0] || null;
    } catch (error) {
      deliveryKey = rawDeliveryKey; // 如果不是JSON，直接使用原始值
    }
    
    if (deliveryKey && this.normalDeliveryData[deliveryKey]) {
      const normalDelivery = this.normalDeliveryData[deliveryKey];
      return normalDelivery;
    }
    
    return null;
  }

  /**
   * 获取变体 metafield 的值
   * @param {string} key metafield 的键（格式：namespace.key）
   * @returns {any} metafield 的值
   */
  getVariantMetafieldValue(key) {
    if (this.currentMetafields[key]) {
      const value = this.currentMetafields[key].value || this.currentMetafields[key];
      return value;
    }
    
    return null;
  }

  /**
   * 更新特惠配送选项 - 已禁用
   */
  updateSpecialDeliveryOptions() {
    /* 已注释特惠配送更新事件
    const specialDelivery = this.getCurrentSpecialDelivery();
    
    // 触发特惠配送更新事件
    document.dispatchEvent(new CustomEvent('special-delivery:updated', {
      detail: {
        variantId: this.currentVariant?.id,
        specialDelivery: specialDelivery,
        metafields: this.currentMetafields
      }
    }));
    */
  }
  
  /**
   * 更新普通配送选项 - 已禁用
   */
  updateNormalDeliveryOptions() {
    /* 已注释普通配送更新事件
    const normalDelivery = this.getCurrentNormalDelivery();
    // 触发普通配送更新事件
    document.dispatchEvent(new CustomEvent('normal-delivery:updated', {
      detail: {
        variantId: this.currentVariant?.id,
        normalDelivery: normalDelivery,
        metafields: this.currentMetafields
      }
    }));
    */
  }

  /**
   * 触发 metafields 更新事件
   */
  dispatchMetafieldsUpdated() {
    document.dispatchEvent(new CustomEvent('variant:metafields:changed', {
      detail: {
        variantId: this.currentVariant?.id,
        variant: this.currentVariant,
        metafields: this.currentMetafields
      }
    }));
  }

  /**
   * 检查是否有特惠配送选项
   * @returns {boolean}
   */
  hasSpecialDelivery() {
    return this.getCurrentSpecialDelivery() !== null;
  }
  
  /**
   * 检查是否有普通配送选项
   * @returns {boolean}
   */
  hasNormalDelivery() {
    return this.getCurrentNormalDelivery() !== null;
  }

  /**
   * 获取特惠配送变体ID
   * @returns {string|null}
   */
  getSpecialDeliveryVariantId() {
    const specialDelivery = this.getCurrentSpecialDelivery();
    return specialDelivery?.variant || null;
  }
  
  /**
   * 获取普通配送变体ID
   * @returns {string|null}
   */
  getNormalDeliveryVariantId() {
    const normalDelivery = this.getCurrentNormalDelivery();
    return normalDelivery?.variant || null;
  }

  /**
   * 获取特惠配送价格
   * @returns {number|null}
   */
  getSpecialDeliveryPrice() {
    const specialDelivery = this.getCurrentSpecialDelivery();
    return specialDelivery?.price || null;
  }
  
  /**
   * 获取普通配送价格
   * @returns {number|null}
   */
  getNormalDeliveryPrice() {
    const normalDelivery = this.getCurrentNormalDelivery();
    return normalDelivery?.price || null;
  }
}

// 创建全局实例
window.variantMetafieldsHandler = new VariantMetafieldsHandler();

// 导出类以供其他模块使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VariantMetafieldsHandler;
} 